import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { formatTravelDate } from '../../utils/dateUtils';
import { 
  COUNTRIES, 
  getCountryInfo, 
  getPostalLabel, 
  getPostalPlaceholder, 
  getStateLabel, 
  isStateRequired, 
  validatePostalCode,
  formatPostalCode 
} from '../../utils/countryUtils';
import './PublicPaymentPage.css';
import './PaymentForm.css';
import PaymentStateScreen from './PaymentStateScreen';

// Create axios instance without auth interceptors for public routes
const publicAxios = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  timeout: 60000 // 60 second timeout for payment processing
});

const UserIcon = () => (<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>);
const CardIcon = () => (<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="1" y="4" width="22" height="16" rx="2" /><line x1="1" y1="10" x2="23" y2="10" /></svg>);
const money = (value) => parseFloat(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const shortDate = (value) => {
  const iso = formatTravelDate(value);
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? new Date(+m[1], +m[2] - 1, +m[3]).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : iso;
};

const PublicPaymentPage = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showSecondPayment, setShowSecondPayment] = useState(false);
  const [agentTerms, setAgentTerms] = useState('');
  const [errors, setErrors] = useState({});
  
  const [primaryPayment, setPrimaryPayment] = useState({
    cardNumber: '',
    cardName: '',
    expiryMonth: '',
    expiryYear: '',
    cvv: '',
    billingAddress: '',
    billingCity: '',
    billingState: '',
    billingZip: '',
    billingCountry: 'US'
  });
  
  const [secondaryPayment, setSecondaryPayment] = useState({
    cardNumber: '',
    cardName: '',
    expiryMonth: '',
    expiryYear: '',
    cvv: '',
    amount: ''
  });
  
  const [contact, setContact] = useState({
    email: '',
    phone: ''
  });
  

  useEffect(() => {
    fetchPaymentSession();
  }, [sessionId]);

  const fetchPaymentSession = async () => {
    try {
      const response = await publicAxios.get(`/public/payment-sessions/${sessionId}`);
      const sessionData = response.data;
      setSession(sessionData);
      setAgentTerms(sessionData.agent_terms || '');
      
      // Auto-fill contact information for group bookings
      if (sessionData.session_type === 'group_booking' && sessionData.participant) {
        setContact({
          email: sessionData.participant.email || '',
          phone: sessionData.participant.phone || ''
        });
        
        // Auto-fill billing address if participant address exists
        if (sessionData.participant.address) {
          // Parse address string (assuming format: "Street, City, State ZIP")
          const addressParts = sessionData.participant.address.split(',');
          const street = addressParts[0]?.trim() || '';
          const cityState = addressParts[1]?.trim() || '';
          const stateZip = addressParts[2]?.trim() || '';
          
          // Extract state and ZIP from last part
          const stateZipMatch = stateZip.match(/^(.+?)\s+(\d{5}(-\d{4})?)$/);
          const state = stateZipMatch ? stateZipMatch[1].trim() : '';
          const zip = stateZipMatch ? stateZipMatch[2] : '';
          
          setPrimaryPayment(prev => ({
            ...prev,
            billingAddress: street,
            billingCity: cityState,
            billingState: state,
            billingZip: zip
          }));
        }
        
        // Auto-fill cardholder name with participant name
        if (sessionData.participant.first_name && sessionData.participant.last_name) {
          setPrimaryPayment(prev => ({
            ...prev,
            cardName: `${sessionData.participant.first_name} ${sessionData.participant.last_name}`
          }));
        }
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching payment session:', err);
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Validate contact info
    if (!contact.email) newErrors.email = 'Email is required';
    if (!contact.phone) newErrors.phone = 'Phone is required';
    
    // Validate primary payment
    if (!primaryPayment.cardNumber) {
      newErrors.cardNumber = 'Card number is required';
    } else if (!/^\d+$/.test(primaryPayment.cardNumber.replace(/\s/g, ''))) {
      newErrors.cardNumber = 'Card number must contain only numbers';
    } else if (primaryPayment.cardNumber.replace(/\s/g, '').length < 13) {
      newErrors.cardNumber = 'Card number must be at least 13 digits';
    }
    if (!primaryPayment.cardName) newErrors.cardName = 'Cardholder name is required';
    if (!primaryPayment.expiryMonth) newErrors.expiryMonth = 'Expiry month is required';
    if (!primaryPayment.expiryYear) newErrors.expiryYear = 'Expiry year is required';
    if (!primaryPayment.cvv) newErrors.cvv = 'CVV is required';
    if (!primaryPayment.billingAddress) newErrors.billingAddress = 'Billing address is required';
    if (!primaryPayment.billingCity) newErrors.billingCity = 'City is required';
    
    // Country-specific validation
    const countryInfo = getCountryInfo(primaryPayment.billingCountry);
    
    // State validation - required for certain countries
    if (isStateRequired(primaryPayment.billingCountry) && !primaryPayment.billingState) {
      newErrors.billingState = `${getStateLabel(primaryPayment.billingCountry)} is required`;
    }
    
    // Postal code validation
    if (!primaryPayment.billingZip) {
      newErrors.billingZip = `${getPostalLabel(primaryPayment.billingCountry)} is required`;
    } else if (!validatePostalCode(primaryPayment.billingZip, primaryPayment.billingCountry)) {
      newErrors.billingZip = `Please enter a valid ${getPostalLabel(primaryPayment.billingCountry).toLowerCase()}`;
    }
    
    // Validate secondary payment if shown
    if (showSecondPayment) {
      if (!secondaryPayment.cardNumber) {
        newErrors.secondCardNumber = 'Second card number is required';
      } else if (!/^\d+$/.test(secondaryPayment.cardNumber.replace(/\s/g, ''))) {
        newErrors.secondCardNumber = 'Card number must contain only numbers';
      } else if (secondaryPayment.cardNumber.replace(/\s/g, '').length < 13) {
        newErrors.secondCardNumber = 'Card number must be at least 13 digits';
      }
      if (!secondaryPayment.cardName) newErrors.secondCardName = 'Second cardholder name is required';
      if (!secondaryPayment.amount || parseFloat(secondaryPayment.amount) <= 0) {
        newErrors.secondAmount = 'Valid amount is required';
      }
      
      const totalAmount = parseFloat(primaryPayment.amount || session.amount) + parseFloat(secondaryPayment.amount || 0);
      if (totalAmount < session.amount) {
        newErrors.secondAmount = `Total must equal $${session.amount}`;
      }
    }
    
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSubmitting(true);
    
    try {
      const response = await publicAxios.post('/public/process-payment', {
        sessionId: sessionId,
        primaryPayment: primaryPayment,
        secondaryPayment: showSecondPayment ? secondaryPayment : null,
        contact: contact,
        agreeToTerms: true // Always true since submission implies agreement
      });
      
      if (response.data.success) {
        navigate(`/payment-success/${response.data.confirmationNumber}`);
      }
    } catch (error) {
      console.error('Payment error:', error);
      
      // Check if this was a timeout or network error
      const isTimeout = error.code === 'ECONNABORTED' || error.message?.includes('timeout');
      const isNetworkError = !error.response;
      
      // If timeout or network error, check if payment actually succeeded
      if (isTimeout || isNetworkError) {
        try {
          console.log('Checking payment status after timeout/network error...');
          const statusCheck = await publicAxios.get(`/public/payment-sessions/${sessionId}/status`);
          
          if (statusCheck.data.status === 'completed' && statusCheck.data.confirmationNumber) {
            // Payment actually succeeded! Redirect to success page
            console.log('Payment was successful despite timeout');
            navigate(`/payment-success/${statusCheck.data.confirmationNumber}`);
            return;
          }
        } catch (statusError) {
          console.error('Error checking payment status:', statusError);
        }
        
        alert('Your payment is being processed. This may take a moment. Please check your email for confirmation, or contact your travel agent if you do not receive confirmation within 5 minutes.');
      } else if (error.response?.status === 409) {
        // Duplicate payment attempt
        alert('This payment has already been processed. Please check your email for confirmation.');
      } else if (error.response?.data?.message) {
        // Show specific error message from server
        alert(error.response.data.message);
      } else {
        // Generic error
        alert('Payment processing failed. Please try again or contact your travel agent for assistance.');
      }
      
      setSubmitting(false);
    }
  };

  const handlePrimaryAmountChange = (value) => {
    if (showSecondPayment) {
      const remaining = session.amount - parseFloat(value || 0);
      setSecondaryPayment(prev => ({ ...prev, amount: remaining > 0 ? remaining.toFixed(2) : '0' }));
    }
  };

  if (loading) return <PaymentStateScreen variant="loading" />;
  if (!session) return <PaymentStateScreen variant="not-found" title="We Couldn't Find This Payment Session" message="It may have expired, or the link may be incorrect." />;

  return (
    <div className="pf-page pf-session">
      <div className="pf-header">
        <div className="pf-header-brand">
          <div className="pf-header-mark" aria-hidden="true">
            <svg viewBox="0 0 24 24"><polygon points="3 11 22 2 13 21 11 13 3 11" /></svg>
          </div>
          <span>Voyager Pro</span>
        </div>
        <h1>Payment Information</h1>
        <p className="pf-header-quote">Quote #{session.quote_number}</p>
        <div className="pf-header-pill">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
          Your payment information is secure and encrypted
        </div>
      </div>

      <div className="pf-container">
        <div className="pf-grid">
          <div className="pf-summary-wrap">
            <aside className="pf-summary">
              <div className="pf-summary-head">
                <div className="pf-summary-eyebrow">Summary</div>
                <div className="pf-summary-title">Payment Information</div>
              </div>
              <div className="pf-summary-body">
                <div className="pf-summary-line">
                  <span>Trip</span>
                  <span>
              {session.session_type === 'group_booking' 
                ? session.group_booking?.title 
                : session.quote_title}
            </span>
                </div>
                <div className="pf-summary-line">
                  <span>Travel Dates</span>
                  <span>
              {session.session_type === 'group_booking' ? (
                session.group_booking?.start_date && session.group_booking?.end_date ? 
                  `${shortDate(session.group_booking.start_date)} – ${shortDate(session.group_booking.end_date)}` : 
                  'TBD'
              ) : (
                session.travelDates?.start && session.travelDates?.end ? 
                  `${shortDate(session.travelDates.start)} – ${shortDate(session.travelDates.end)}` : 
                  session.travel_start_date && session.travel_end_date ?
                    `${shortDate(session.travel_start_date)} – ${shortDate(session.travel_end_date)}` :
                    'TBD'
              )}
            </span>
                </div>
                <div className="pf-summary-line">
                  <span>Total Cost</span>
                  <span>${money(session.total_amount)}</span>
                </div>
                <div className="pf-due">
                  <span>Amount Due Now</span>
                  <span className="pf-due-amount">${money(session.amount)}</span>
                </div>
                <div className="pf-summary-line">
                  <span>Payment Type</span>
                  <span>{session.payment_type === 'deposit' ? 'Deposit' : 'Full Payment'}</span>
                </div>
                {session.payment_type === 'deposit' && (
                  <div className="pf-balance">
                    Remaining balance of <strong>${money(parseFloat(session.total_amount) - parseFloat(session.amount))}</strong> will be due before travel.
                  </div>
                )}
              </div>
            </aside>
          </div>

          <form onSubmit={handleSubmit} className="pf-form">
          <div className="pf-card">
            <div className="pf-card-head">
              <div className="pf-card-head-title"><UserIcon />Contact Information</div>
            </div>
            <div className="pf-card-body">
          <div className="pf-row">
            <div className="pf-field">
              <label>Email Address<span className="pf-req">*</span></label>
              <input
                type="email"
                value={contact.email}
                onChange={(e) => setContact({...contact, email: e.target.value})}
                className={errors.email ? 'is-invalid' : ''}
                required
              />
              {errors.email && <span className="pf-error">{errors.email}</span>}
            </div>
            <div className="pf-field">
              <label>Phone Number<span className="pf-req">*</span></label>
              <input
                type="tel"
                value={contact.phone}
                onChange={(e) => setContact({...contact, phone: e.target.value})}
                className={errors.phone ? 'is-invalid' : ''}
                required
              />
              {errors.phone && <span className="pf-error">{errors.phone}</span>}
            </div>
          </div>

            </div>
          </div>

          <div className="pf-card">
            <div className="pf-card-head">
              <div className="pf-card-head-title"><CardIcon />Payment Method{showSecondPayment ? ' 1' : ''}</div>
            </div>
            <div className="pf-card-body">
          <div className="pf-preview" aria-hidden="true">
            <div>
              <div className="pf-preview-label">Card Number</div>
              <div className="pf-preview-number">{primaryPayment.cardNumber || '•••• •••• •••• ••••'}</div>
            </div>
            <div className="pf-preview-dots"><span /><span /></div>
          </div>

          <div className="pf-field">
            <label>Cardholder Name<span className="pf-req">*</span></label>
            <input
              type="text"
              value={primaryPayment.cardName}
              onChange={(e) => setPrimaryPayment({...primaryPayment, cardName: e.target.value})}
              className={errors.cardName ? 'is-invalid' : ''}
              required
            />
            {errors.cardName && <span className="pf-error">{errors.cardName}</span>}
          </div>

          <div className="pf-field pf-mono">
            <label>Card Number<span className="pf-req">*</span></label>
            <input
              type="text"
              value={primaryPayment.cardNumber}
              onChange={(e) => setPrimaryPayment({...primaryPayment, cardNumber: e.target.value})}
              placeholder="1234 5678 9012 3456"
              className={errors.cardNumber ? 'is-invalid' : ''}
              required
            />
            {errors.cardNumber && <span className="pf-error">{errors.cardNumber}</span>}
          </div>

          <div className="pf-row">
            <div className="pf-field">
              <label>Expiry Date<span className="pf-req">*</span></label>
              <div className="pf-expiry">
                <input
                  type="text"
                  placeholder="MM"
                  maxLength="2"
                  value={primaryPayment.expiryMonth}
                  onChange={(e) => setPrimaryPayment({...primaryPayment, expiryMonth: e.target.value})}
                  className={errors.expiryMonth ? 'is-invalid' : ''}
                  required
                />
                <span>/</span>
                <input
                  type="text"
                  placeholder="YY"
                  maxLength="2"
                  value={primaryPayment.expiryYear}
                  onChange={(e) => setPrimaryPayment({...primaryPayment, expiryYear: e.target.value})}
                  className={errors.expiryYear ? 'is-invalid' : ''}
                  required
                />
              </div>
              {(errors.expiryMonth || errors.expiryYear) && 
                <span className="pf-error">Valid expiry date required</span>}
            </div>
            <div className="pf-field">
              <label>CVV<span className="pf-req">*</span></label>
              <input
                type="text"
                maxLength="4"
                value={primaryPayment.cvv}
                onChange={(e) => setPrimaryPayment({...primaryPayment, cvv: e.target.value})}
                className={errors.cvv ? 'is-invalid' : ''}
                required
              />
              {errors.cvv && <span className="pf-error">{errors.cvv}</span>}
            </div>
          </div>

          {showSecondPayment && (
            <div className="pf-field">
              <label>Amount on this card<span className="pf-req">*</span></label>
              <input
                type="number"
                step="0.01"
                value={primaryPayment.amount || session.amount}
                onChange={(e) => {
                  setPrimaryPayment({...primaryPayment, amount: e.target.value});
                  handlePrimaryAmountChange(e.target.value);
                }}
                required
              />
            </div>
          )}

          <h3>Billing Address</h3>
          
          <div className="pf-field">
            <label>Country<span className="pf-req">*</span></label>
            <select
              value={primaryPayment.billingCountry}
              onChange={(e) => {
                const newCountry = e.target.value;
                setPrimaryPayment({
                  ...primaryPayment, 
                  billingCountry: newCountry,
                  billingState: '', // Reset state when country changes
                  billingZip: '' // Reset postal code when country changes
                });
              }}
              className={errors.billingCountry ? 'is-invalid' : ''}
              required
            >
              {COUNTRIES.map(country => (
                <option key={country.code} value={country.code}>
                  {country.name}
                </option>
              ))}
            </select>
            {errors.billingCountry && <span className="pf-error">{errors.billingCountry}</span>}
          </div>
          
          <div className="pf-field">
            <label>Street Address<span className="pf-req">*</span></label>
            <input
              type="text"
              value={primaryPayment.billingAddress}
              onChange={(e) => setPrimaryPayment({...primaryPayment, billingAddress: e.target.value})}
              className={errors.billingAddress ? 'is-invalid' : ''}
              required
            />
            {errors.billingAddress && <span className="pf-error">{errors.billingAddress}</span>}
          </div>

          <div className="pf-row">
            <div className="pf-field">
              <label>City<span className="pf-req">*</span></label>
              <input
                type="text"
                value={primaryPayment.billingCity}
                onChange={(e) => setPrimaryPayment({...primaryPayment, billingCity: e.target.value})}
                className={errors.billingCity ? 'is-invalid' : ''}
                required
              />
              {errors.billingCity && <span className="pf-error">{errors.billingCity}</span>}
            </div>
            <div className="pf-field">
              <label>
                {getStateLabel(primaryPayment.billingCountry)}
                {isStateRequired(primaryPayment.billingCountry) ? <span className="pf-req">*</span> : ''}
              </label>
              <input
                type="text"
                value={primaryPayment.billingState}
                onChange={(e) => setPrimaryPayment({...primaryPayment, billingState: e.target.value})}
                maxLength={primaryPayment.billingCountry === 'US' ? '2' : '50'}
                placeholder={primaryPayment.billingCountry === 'US' ? 'CA' : 'Optional'}
                className={errors.billingState ? 'is-invalid' : ''}
                required={isStateRequired(primaryPayment.billingCountry)}
              />
              {errors.billingState && <span className="pf-error">{errors.billingState}</span>}
            </div>
            <div className="pf-field">
              <label>{getPostalLabel(primaryPayment.billingCountry)}<span className="pf-req">*</span></label>
              <input
                type="text"
                value={primaryPayment.billingZip}
                onChange={(e) => {
                  const formatted = formatPostalCode(e.target.value, primaryPayment.billingCountry);
                  setPrimaryPayment({...primaryPayment, billingZip: formatted});
                }}
                placeholder={getPostalPlaceholder(primaryPayment.billingCountry)}
                className={errors.billingZip ? 'is-invalid' : ''}
                required
              />
              {errors.billingZip && <span className="pf-error">{errors.billingZip}</span>}
            </div>
          </div>

            </div>
          </div>

          {!showSecondPayment && (
            <div>
              <button type="button" onClick={() => setShowSecondPayment(true)} className="pf-add-method">
                <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="10" /><path d="M12 8v8M8 12h8" /></svg>
                Add Second Payment Method
              </button>
            </div>
          )}

          {showSecondPayment && (
            <div className="pf-card">
              <div className="pf-card-head">
                <div className="pf-card-head-title"><CardIcon />Payment Method 2</div>
                <button type="button" onClick={() => setShowSecondPayment(false)} className="pf-remove">
                  Remove
                </button>
              </div>
              <div className="pf-card-body">

              <div className="pf-field">
                <label>Amount on second card<span className="pf-req">*</span></label>
                <input
                  type="number"
                  step="0.01"
                  value={secondaryPayment.amount}
                  onChange={(e) => {
                    setSecondaryPayment({...secondaryPayment, amount: e.target.value});
                    const primaryAmount = session.amount - parseFloat(e.target.value || 0);
                    setPrimaryPayment(prev => ({ ...prev, amount: primaryAmount > 0 ? primaryAmount.toFixed(2) : '0' }));
                  }}
                  className={errors.secondAmount ? 'is-invalid' : ''}
                  required
                />
                {errors.secondAmount && <span className="pf-error">{errors.secondAmount}</span>}
              </div>

              <div className="pf-field">
                <label>Cardholder Name<span className="pf-req">*</span></label>
                <input
                  type="text"
                  value={secondaryPayment.cardName}
                  onChange={(e) => setSecondaryPayment({...secondaryPayment, cardName: e.target.value})}
                  className={errors.secondCardName ? 'is-invalid' : ''}
                  required
                />
                {errors.secondCardName && <span className="pf-error">{errors.secondCardName}</span>}
              </div>

              <div className="pf-field pf-mono">
                <label>Card Number<span className="pf-req">*</span></label>
                <input
                  type="text"
                  value={secondaryPayment.cardNumber}
                  onChange={(e) => setSecondaryPayment({...secondaryPayment, cardNumber: e.target.value})}
                  placeholder="1234 5678 9012 3456"
                  className={errors.secondCardNumber ? 'is-invalid' : ''}
                  required
                />
                {errors.secondCardNumber && <span className="pf-error">{errors.secondCardNumber}</span>}
              </div>

              <div className="pf-row">
                <div className="pf-field">
                  <label>Expiry Date<span className="pf-req">*</span></label>
                  <div className="pf-expiry">
                    <input
                      type="text"
                      placeholder="MM"
                      maxLength="2"
                      value={secondaryPayment.expiryMonth}
                      onChange={(e) => setSecondaryPayment({...secondaryPayment, expiryMonth: e.target.value})}
                      required
                    />
                    <span>/</span>
                    <input
                      type="text"
                      placeholder="YY"
                      maxLength="2"
                      value={secondaryPayment.expiryYear}
                      onChange={(e) => setSecondaryPayment({...secondaryPayment, expiryYear: e.target.value})}
                      required
                    />
                  </div>
                </div>
                <div className="pf-field">
                  <label>CVV<span className="pf-req">*</span></label>
                  <input
                    type="text"
                    maxLength="4"
                    value={secondaryPayment.cvv}
                    onChange={(e) => setSecondaryPayment({...secondaryPayment, cvv: e.target.value})}
                    required
                  />
                </div>
              </div>
              </div>
            </div>
          )}

          {agentTerms && (
            <div className="pf-terms">
              <h3>Travel Agency Terms & Conditions</h3>
              <div className="pf-terms-body" dangerouslySetInnerHTML={{ __html: agentTerms }} />
            </div>
          )}

          <div className="pf-agreement">
            <p>
              By submitting your payment information you acknowledge that you have read and agreed to the Terms and Conditions of this quote.
            </p>
          </div>

          <button type="submit" className="pf-submit" disabled={submitting}>
            {submitting ? 'Submitting...' : `Submit Payment Info ($${money(session.amount)})`}
          </button>
        </form>
        </div>
      </div>
    </div>
  );
};

export default PublicPaymentPage;
